import React, { useEffect, useState } from "react";
import { TouchableOpacity, Text, View, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import customAxios from "../../../utils/customAxios";
import i18n from "../../../locales/i18n";
import { Toast } from "react-native-toast-notifications";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import Animated, { FadeIn, FadeInDown } from "react-native-reanimated";

interface WeeklyStatsData {
  weeklyRevenue: number;
  startDate: string;
}

const DynamicStatsCard = () => {
  const locale = i18n.locale;
  const router = useRouter();
  const { statsRefresh } = useSelector((state: RootState) => state.app);
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState<WeeklyStatsData | null>(null);
  const [error, setError] = useState(false);

  // Helper function to get week dates
  const getWeekDates = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)); // Monday as start

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday as end

    return {
      start: startOfWeek,
      end: endOfWeek,
    };
  };

  // Helper function to format date range
  const formatDateRange = (startDate: Date, endDate: Date) => {
    const formatDate = (date: Date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      return `${day}/${month}`;
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const fetchWeeklyStats = async () => {
    setLoading(true);
    setError(false);

    try {
      const { start: currentWeekStart, end: currentWeekEnd } = getWeekDates();

      const formatIntervalDate = (d: Date) =>
        `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getDate().toString().padStart(2, "0")}/${d.getFullYear()}`;

      const res = await customAxios
        .get("/statistic_intervalle", {
          params: {
            start: formatIntervalDate(currentWeekStart),
            end: formatIntervalDate(currentWeekEnd),
          },
          withCredentials: true,
        })
        .catch(() => ({ data: { total: 0 } }));

      const weeklyRevenue = res.data?.total || 0;

      setStatsData({
        weeklyRevenue,
        startDate: formatDateRange(currentWeekStart, currentWeekEnd),
      });
    } catch (error) {
      setError(true);
      if (statsData !== null) {
        Toast.show(i18n.t("findBillsError"), {
          type: "danger",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Add a small delay to ensure other data is loaded first
    const timer = setTimeout(() => {
      fetchWeeklyStats();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Listen for stats refresh trigger
  useEffect(() => {
    if (statsRefresh) {
      fetchWeeklyStats();
    }
  }, [statsRefresh]);

  // Loading skeleton
  if (loading) {
    return (
      <TouchableOpacity
        className="w-full bg-white rounded-xl p-4 mb-4"
        onPress={() => router.push("/(drawer)/statistics")}
        style={{
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 5,
        }}
      >
        {/* Header */}
        <View className="mb-2">
          <View className="bg-gray-200 h-4 w-24 rounded" />
        </View>

        {/* Main Revenue Display */}
        <View className="mb-3">
          <View className="bg-gray-200 h-8 w-32 rounded mb-2" />
          <View className="bg-gray-200 h-4 w-40 rounded" />
        </View>

        {/* Footer */}
        <View
          className="flex-row justify-between items-center"
          style={{
            flexDirection: locale === "ar" ? "row-reverse" : "row",
          }}
        >
          <View className="flex-row items-center">
            <ActivityIndicator size="small" color="#0596D0" />
            <View className="bg-gray-200 h-4 w-20 rounded ml-2" />
          </View>
          <View className="bg-gray-200 h-4 w-16 rounded" />
        </View>
      </TouchableOpacity>
    );
  }

  // Error state
  if (error || !statsData) {
    return (
      <TouchableOpacity
        className="w-full bg-white rounded-xl p-4 mb-4"
        onPress={() => {
          fetchWeeklyStats(); // Retry on tap
        }}
        style={{
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 5,
        }}
      >
        <View className="flex items-center justify-center py-4">
          <Ionicons name="refresh" size={24} color="#666" />
          <Text className="text-gray-600 text-sm mt-2 text-center">
            {i18n.t("tapToRetry")}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }

  return (
    <Animated.View entering={FadeInDown.duration(500)} className="w-full">
      <TouchableOpacity
        className="w-full bg-white rounded-xl p-4 mb-4"
        activeOpacity={0.85}
        onPress={() => router.push("/(drawer)/statistics")}
        style={{
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 5,
        }}
      >
        {/* Header */}
        <View className="mb-2">
          <Text
            className="text-gray-600 text-sm font-medium"
            style={{ textAlign: locale === "ar" ? "right" : "left" }}
          >
            {i18n.t("weekrecap")}
          </Text>
        </View>

        {/* Main Revenue Display */}
        <View className="mb-3">
          <Text
            className="text-2xl font-bold text-black mb-1"
            style={{ textAlign: locale === "ar" ? "right" : "left" }}
          >
            {`${statsData.weeklyRevenue.toLocaleString()} ${i18n.t("currency")}`}
          </Text>
        </View>

        {/* Date Range and Show More */}
        <View
          className="flex-row justify-between items-center"
          style={{
            flexDirection: locale === "ar" ? "row-reverse" : "row",
          }}
        >
          <TouchableOpacity
            onPress={() => router.push("/(drawer)/statistics")}
            className="flex items-center flex-row gap-x-1.5"
            style={{
              flexDirection: locale === "ar" ? "row-reverse" : "row",
            }}
          >
            <Ionicons name="eye" size={16} color="#0596D0" />
            <Text className="text-blue-600 font-medium text-sm">
              {i18n.t("showmore")}
            </Text>
          </TouchableOpacity>

          <Text className="text-gray-500 text-xs">{statsData.startDate}</Text>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
};

export default DynamicStatsCard;
